import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";

const EMPTY_MEETING = {
  actions: "", agenda: "", attachments: "", date: "", deadline: "",
  decisions: "", facilitator: "", location: "", minutesValidated: "no",
  participants: "", responsible: "", type: "",
};
const EMPTY_PROCEDURE = {
  applicationScope: "", documents: "", frequency: "", object: "",
  responsibilities: "", steps: "",
};
const SEARCH_FIELDS = [
  "type", "date", "location", "facilitator", "participants", "agenda",
  "decisions", "actions", "responsible", "deadline", "attachments",
];
const MULTILINE_FIELDS = new Set(["participants", "agenda", "decisions", "actions", "attachments"]);
const COLUMNS = [
  ["N°", 55], ["Type", 125], ["Date", 105], ["Lieu", 120],
  ["Animateur", 140], ["Participants", 165], ["ODJ", 175],
  ["Décisions", 175], ["Actions", 175], ["Responsable", 140],
  ["Échéance", 105], ["PV validé", 100], ["PJ", 135], ["Act.", 90],
];
const PROCEDURE_FIELDS = [
  ["object", "Objet"], ["applicationScope", "Domaine d'application"],
  ["responsibilities", "Responsabilités"], ["steps", "Étapes"],
  ["documents", "Documents de référence"], ["frequency", "Périodicité"],
];
const normalize = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr");
const createId = () => `meeting-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const textareaSx = {
  backgroundColor: "#fff", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "6px", boxSizing: "border-box", color: OUTIL_COLORS.text,
  fontFamily: "'DM Sans', sans-serif", fontSize: 11.5, lineHeight: 1.45,
  minHeight: 68, outline: "none", p: 1, resize: "vertical", width: "100%",
  "&:disabled": { backgroundColor: "#f3f6f9", color: OUTIL_COLORS.textMuted },
  "&:focus": { borderColor: OUTIL_COLORS.navy, boxShadow: "0 0 0 2px rgba(23,61,97,.12)" },
};
const goldButtonSx = {
  backgroundColor: OUTIL_COLORS.gold, color: OUTIL_COLORS.text,
  fontSize: 10, fontWeight: 700, textTransform: "none",
  "&:hover": { backgroundColor: "#c79c2f" },
};
const FieldLabel = ({ children, htmlFor }) => (
  <Typography
    color="#526b84" component="label" display="block" fontSize={9.5}
    fontWeight={700} htmlFor={htmlFor} letterSpacing=".04em" mb={0.45}
    textTransform="uppercase"
  >
    {children}
  </Typography>
);
const MeetingField = ({ field, label, onChange, type = "text", value }) => (
  <Box>
    <FieldLabel htmlFor={`meeting-${field}`}>{label}</FieldLabel>
    {MULTILINE_FIELDS.has(field) ? (
      <Box
        aria-label={label} component="textarea" id={`meeting-${field}`}
        onChange={(event) => onChange(field, event.target.value)}
        rows={3} sx={textareaSx} value={value || ""}
      />
    ) : (
      <TextField
        fullWidth id={`meeting-${field}`}
        onChange={(event) => onChange(field, event.target.value)}
        size="small" type={type} value={value || ""}
      />
    )}
  </Box>
);

const MeetingDialog = ({ form, onChange, onClose, onDelete, onSave, open }) => (
  <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open} PaperProps={{ sx: { borderRadius: "14px", maxHeight: "92vh" } }}>
    <DialogTitle sx={{ alignItems: "center", borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700, justifyContent: "space-between", px: 2.2, py: 1.4 }}>
      <Box component="span">
        <DescriptionOutlinedIcon sx={{ color: "#8c7aa8", fontSize: 19, mr: 0.8, verticalAlign: "middle" }} />
        {form.id ? "Modifier la réunion" : "Nouvelle réunion"}
      </Box>
      <Button color="error" onClick={onClose} size="small" startIcon={<CloseIcon />} sx={{ textTransform: "none" }} variant="outlined">Fermer</Button>
    </DialogTitle>
    <DialogContent sx={{ p: "18px !important" }}>
      <Box sx={{ display: "grid", gap: 1.3, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}>
        <MeetingField field="type" label="Type" onChange={onChange} value={form.type} />
        <MeetingField field="date" label="Date" onChange={onChange} type="date" value={form.date} />
        <MeetingField field="location" label="Lieu" onChange={onChange} value={form.location} />
        <MeetingField field="facilitator" label="Animateur" onChange={onChange} value={form.facilitator} />
        <MeetingField field="participants" label="Participants" onChange={onChange} value={form.participants} />
        <MeetingField field="agenda" label="Ordre du jour (ODJ)" onChange={onChange} value={form.agenda} />
        <MeetingField field="decisions" label="Décisions" onChange={onChange} value={form.decisions} />
        <MeetingField field="actions" label="Actions" onChange={onChange} value={form.actions} />
        <MeetingField field="responsible" label="Responsable" onChange={onChange} value={form.responsible} />
        <MeetingField field="deadline" label="Échéance" onChange={onChange} type="date" value={form.deadline} />
        <Box>
          <FieldLabel htmlFor="meeting-minutes">PV validé</FieldLabel>
          <TextField
            fullWidth id="meeting-minutes" select size="small"
            onChange={(event) => onChange("minutesValidated", event.target.value)}
            value={form.minutesValidated || "no"}
          >
            <MenuItem value="no">Non</MenuItem>
            <MenuItem value="yes">Oui</MenuItem>
          </TextField>
        </Box>
        <MeetingField field="attachments" label="Pièces jointes (PJ)" onChange={onChange} value={form.attachments} />
      </Box>
    </DialogContent>
    <DialogActions sx={{ borderTop: `1px solid ${OUTIL_COLORS.border}`, px: 2.2, py: 1.25 }}>
      {form.id && <Button color="error" onClick={onDelete} startIcon={<DeleteOutlineIcon />} sx={{ mr: "auto", textTransform: "none" }}>Supprimer</Button>}
      <Button onClick={onClose} sx={{ color: OUTIL_COLORS.text, textTransform: "none" }}>Annuler</Button>
      <Button
        onClick={onSave} startIcon={<SaveOutlinedIcon />} variant="contained"
        sx={{ backgroundColor: "#27884f", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "#1f7040" } }}
      >
        Valider et sauvegarder
      </Button>
    </DialogActions>
  </Dialog>
);

const ProcedureForm = ({ onChange, value }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_PROCEDURE, ...(value.meetingProcedure || {}) });

  useEffect(() => {
    setDraft({ ...EMPTY_PROCEDURE, ...(value.meetingProcedure || {}) });
    setEditing(false);
  }, [value.meetingProcedure]);

  const save = () => {
    onChange("meetingProcedure", { ...draft });
    setEditing(false);
  };
  const reset = () => {
    const empty = { ...EMPTY_PROCEDURE };
    setDraft(empty);
    onChange("meetingProcedure", empty);
    setEditing(true);
  };

  return (
    <Paper aria-label="Fiche procédure de réunion" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
      <Box sx={{ alignItems: { xs: "flex-start", sm: "center" }, backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, justifyContent: "space-between", minHeight: 48, px: 1.75, py: 0.8 }}>
        <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
          <DescriptionOutlinedIcon sx={{ fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
          Procédure de réunion
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Button disabled={editing} onClick={() => setEditing(true)} size="small" startIcon={<EditOutlinedIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, textTransform: "none" }} variant="outlined">Modifier</Button>
          <Button disabled={!editing} onClick={save} size="small" startIcon={<SaveOutlinedIcon />} sx={{ backgroundColor: "#27884f", fontSize: 10, textTransform: "none", "&:hover": { backgroundColor: "#1f7040" } }} variant="contained">Enregistrer</Button>
          <Button onClick={reset} size="small" startIcon={<RestartAltIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="outlined">Réinitialiser</Button>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gap: 1.35, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, p: 1.75 }}>
        {PROCEDURE_FIELDS.map(([field, label]) => (
          <Box key={field}>
            <FieldLabel htmlFor={`procedure-${field}`}>{label}</FieldLabel>
            <Box
              aria-label={label} component="textarea" disabled={!editing}
              id={`procedure-${field}`}
              onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
              rows={field === "steps" || field === "responsibilities" ? 4 : 3}
              sx={textareaSx} value={draft[field] || ""}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const ProcedureReunion = ({ onChange, value = {} }) => {
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_MEETING });
  const rows = useMemo(() => (
    Array.isArray(value.meetingRows) ? value.meetingRows : []
  ), [value.meetingRows]);
  const filteredRows = useMemo(() => {
    const query = normalize(search).trim();
    return query
      ? rows.filter((row) => SEARCH_FIELDS.some((field) => normalize(row[field]).includes(query)))
      : rows;
  }, [rows, search]);

  const setRows = (nextRows) => onChange("meetingRows", nextRows);
  const openCreate = () => {
    setForm({ ...EMPTY_MEETING });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY_MEETING, ...row });
    setDialogOpen(true);
  };
  const saveMeeting = () => {
    setRows(form.id
      ? rows.map((row) => (row.id === form.id ? { ...form } : row))
      : [...rows, { ...form, id: createId() }]);
    setDialogOpen(false);
  };
  const deleteMeeting = () => {
    if (form.id) setRows(rows.filter((row) => row.id !== form.id));
    setDialogOpen(false);
  };

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#e8f0f1", border: `1px solid ${OUTIL_COLORS.border}`, borderLeft: `3px solid ${OUTIL_COLORS.gold}`, borderRadius: "7px", display: "flex", flexWrap: "wrap", gap: 1.2, px: 1.5, py: 1 }}>
        <DescriptionOutlinedIcon sx={{ color: "#8c7aa8", fontSize: 18 }} />
        <Typography color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={700}>OUTIL_8_Procedure_reunion.docx</Typography>
        <Tooltip title="Téléchargement disponible ultérieurement">
          <span><Button disabled size="small" startIcon={<FileDownloadOutlinedIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="contained">Télécharger le template</Button></span>
        </Tooltip>
      </Paper>

      <Paper aria-label="Registre des réunions" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", justifyContent: "space-between", minHeight: 48, px: 1.75 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <DescriptionOutlinedIcon sx={{ fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
            Procédure de réunion — §7.5
          </Typography>
          <Button onClick={openCreate} size="small" startIcon={<AddIcon />} sx={goldButtonSx} variant="contained">Ajouter</Button>
        </Box>
        <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
          <TextField
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrer toutes les colonnes..." size="small"
            sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
            value={search}
          />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} / {rows.length} entrée(s)</Typography>
          <Button disabled={!search} onClick={() => setSearch("")} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>Réinit.</Button>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table aria-label="Tableau des procédures de réunion" size="small" sx={{ minWidth: 1850, tableLayout: "fixed" }}>
            <TableHead><TableRow>{COLUMNS.map(([label, width]) => <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {!filteredRows.length ? (
                <TableRow><TableCell align="center" colSpan={COLUMNS.length} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>Aucune réunion enregistrée — cliquez sur Ajouter.</TableCell></TableRow>
              ) : filteredRows.map((row) => {
                const number = rows.findIndex((item) => item.id === row.id) + 1;
                const values = [
                  number, row.type, row.date, row.location, row.facilitator,
                  row.participants, row.agenda, row.decisions, row.actions,
                  row.responsible, row.deadline,
                ];
                return (
                  <TableRow hover key={row.id} onClick={() => openEdit(row)} sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f7f9fc" } }}>
                    {values.map((cell, index) => <TableCell key={COLUMNS[index][0]}>{cell || "—"}</TableCell>)}
                    <TableCell>
                      <Chip
                        label={row.minutesValidated === "yes" ? "Oui" : "Non"} size="small"
                        sx={{
                          backgroundColor: row.minutesValidated === "yes" ? "#d9f7e5" : "#fde7e7",
                          color: row.minutesValidated === "yes" ? "#147a43" : "#a92824",
                          fontSize: 9.5, fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.attachments || "—"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Modifier">
                          <IconButton aria-label={`Modifier la réunion ${number}`} onClick={(event) => { event.stopPropagation(); openEdit(row); }} size="small" sx={{ backgroundColor: OUTIL_COLORS.navy, borderRadius: "6px", color: "#fff" }}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton aria-label={`Supprimer la réunion ${number}`} onClick={(event) => { event.stopPropagation(); setRows(rows.filter((item) => item.id !== row.id)); }} size="small" sx={{ backgroundColor: "#b42025", borderRadius: "6px", color: "#fff" }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ProcedureForm onChange={onChange} value={value} />
      <MeetingDialog
        form={form}
        onChange={(field, nextValue) => setForm((current) => ({ ...current, [field]: nextValue }))}
        onClose={() => setDialogOpen(false)}
        onDelete={deleteMeeting}
        onSave={saveMeeting}
        open={dialogOpen}
      />
    </Box>
  );
};

export default ProcedureReunion;